/** Claim list (spec §8.8). */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { ClaimDto } from '@ias/shared';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DataTable, type DataColumn } from '@/components/data-table/data-table';
import { EntityCell } from '@/components/common/entity-avatar';
import { EnumBadge } from '@/components/common/enum-badge';
import { PageHeader } from '@/components/common/page-header';
import { FilterBar, type FilterDescriptor } from '@/components/filters/filter-bar';
import { claimsApi, useInsuranceCompanies } from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { useListState } from '@/hooks/use-list-state';

import { ClaimFormDialog } from './claim-form';

export function ClaimListPage() {
  const { t, money, date } = useI18n();
  const navigate = useNavigate();
  const { can } = useAuth();

  const list = useListState({ defaultSortBy: 'incidentDate', defaultSortOrder: 'desc' });
  const { data, isLoading, isFetching, error, refetch } = claimsApi.useList(list.params);
  const { data: insurers } = useInsuranceCompanies();
  const [formOpen, setFormOpen] = React.useState(false);

  const totals = (data?.meta as { totals?: { claimed: string; paidOut: string } } | undefined)?.totals;

  const columns = React.useMemo<DataColumn<ClaimDto>[]>(
    () => [
      {
        id: 'claimNumber',
        header: t('claim.number'),
        meta: { sortKey: 'claimNumber', headerLabel: t('claim.number') },
        cell: ({ row }) => (
          <span className="font-medium tabular">
            {row.original.claimNumber ?? row.original.id.slice(0, 8)}
          </span>
        ),
      },
      {
        id: 'clientName',
        header: t('client.one'),
        meta: { sortKey: 'clientName', headerLabel: t('client.one') },
        cell: ({ row }) => <span className="truncate font-medium">{row.original.clientName}</span>,
      },
      {
        id: 'insuranceCompanyName',
        header: t('policy.company'),
        meta: { sortKey: 'insuranceCompanyName', hideBelow: 'lg', headerLabel: t('policy.company') },
        cell: ({ row }) => <EntityCell name={row.original.insuranceCompanyName} muted />,
      },
      {
        id: 'incidentDate',
        header: t('claim.incidentDate'),
        meta: { sortKey: 'incidentDate', numeric: true, headerLabel: t('claim.incidentDate') },
        cell: ({ row }) => date(row.original.incidentDate),
      },
      {
        id: 'claimAmount',
        header: t('claim.amount'),
        meta: { sortKey: 'claimAmount', align: 'right', numeric: true, headerLabel: t('claim.amount') },
        cell: ({ row }) => money(row.original.claimAmount),
      },
      {
        id: 'payoutAmount',
        header: t('claim.payout'),
        meta: {
          sortKey: 'payoutAmount',
          align: 'right',
          numeric: true,
          hideBelow: 'md',
          headerLabel: t('claim.payout'),
        },
        cell: ({ row }) =>
          row.original.payoutAmount ? (
            <span className="text-good font-medium">{money(row.original.payoutAmount)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: 'status',
        header: t('policy.status'),
        meta: { sortKey: 'status', headerLabel: t('policy.status') },
        cell: ({ row }) => <EnumBadge enum="claimStatus" value={row.original.status} size="sm" />,
      },
      {
        id: 'daysOpen',
        header: t('claim.daysOpen'),
        meta: { sortKey: 'daysOpen', align: 'center', numeric: true, hideBelow: 'md', headerLabel: t('claim.daysOpen') },
        cell: ({ row }) => {
          const days = row.original.daysOpen ?? 0;
          const closed = Boolean(row.original.closedAt);
          // Ageing only escalates while the file is still open.
          const tone = closed ? 'outline' : days > 90 ? 'critical' : days > 30 ? 'serious' : 'neutral';
          return (
            <Badge tone={tone} size="sm" className="tabular">
              {days}
            </Badge>
          );
        },
      },
    ],
    [t, money, date],
  );

  /*
   * One declaration, two homes: a filter naming a `column` is rendered in the table's
   * filter row under that column, the rest stay in the toolbar, and the narrow-screen
   * popover keeps all of them.
   */
  const filters: FilterDescriptor[] = [
    { key: 'status', type: 'enum', enum: 'claimStatus', label: t('policy.status'), multiple: true, column: 'status' },
    {
      key: 'insuranceCompanyId',
      column: 'insuranceCompanyName',
      type: 'options',
      label: t('policy.company'),
      options: (insurers ?? []).map((insurer) => ({ value: insurer.id, label: insurer.name })),
    },
    { key: 'incidentDateFrom', type: 'date', label: t('common.from'), column: 'incidentDate' },
    { key: 'incidentDateTo', type: 'date', label: t('common.to') },
  ];

  return (
    <>
      <PageHeader
        title={t('claim.title')}
        description={t('claim.subtitle')}
        actions={
          can('claim:write') && (
            <Button onClick={() => setFormOpen(true)}>
              <Plus /> {t('action.newClaim')}
            </Button>
          )
        }
      />

      <FilterBar
        className="mb-5"
        listState={list}
        inline="unbound"
        searchPlaceholder={`${t('claim.number')} / ${t('client.one')}`}
        filters={filters}
      />

      <DataTable
        columns={columns}
        rows={data?.data}
        total={data?.meta.total}
        listState={list}
        filters={filters}
        isLoading={isLoading}
        isFetching={isFetching}
        error={error}
        onRetry={refetch}
        getRowId={(claim) => claim.id}
        onRowClick={(claim) => navigate(`/claims/${claim.id}`)}
        footer={
          totals && (
            <>
              <span className="text-muted-foreground">
                {t('claim.amount')}{' '}
                <span className="text-foreground font-semibold tabular">{money(totals.claimed)}</span>
              </span>
              <span className="text-muted-foreground">
                {t('claim.payout')}{' '}
                <span className="text-good font-semibold tabular">{money(totals.paidOut)}</span>
              </span>
            </>
          )
        }
      />

      <ClaimFormDialog open={formOpen} onOpenChange={setFormOpen} />
    </>
  );
}
